import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Hero from "@/components/Hero";
import HowItWorks from "@/components/HowItWorks";
import ArticlesGrid from "@/components/ArticlesGrid";
import About from "@/components/About";
import Newsletter from "@/components/Newsletter";
import SeoHead from "@/components/SeoHead";

export default function HomePage() {
  const location = useLocation();

  // If the URL contains a hash (e.g. arrived from /archive -> /#about), scroll to it once content is stable.
  // Uses a retry loop to survive async content growth (ArticlesGrid fetches asynchronously and expands the page height).
  useEffect(() => {
    if (!location.hash) return;
    let attempts = 0;
    let lastTop = null;
    const tick = () => {
      attempts++;
      const el = document.querySelector(location.hash);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // Keep retrying until the target's absolute Y stops changing (content settled) — cap at 10 attempts (~2s).
        if (top !== lastTop && attempts < 10) {
          lastTop = top;
          setTimeout(tick, 200);
        }
      } else if (attempts < 10) {
        setTimeout(tick, 200);
      }
    };
    // Small initial delay so the browser has painted the first frame.
    const t = setTimeout(tick, 60);
    return () => clearTimeout(t);
  }, [location.hash, location.pathname]);

  return (
    <div className="min-h-screen topo-bg relative" data-testid="home-page">
      <SeoHead
        title=""
        description="Essays and guides on mind mapping, book summaries, and visual learning. Learn how to map any book clearly and retain what you read."
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "SummaryMaps",
            "url": "https://summarymaps.com",
            "logo": "https://summarymapsjournal.com/logo.png",
            "sameAs": ["https://summarymapsjournal.com"],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "SummaryMaps Journal",
            "url": "https://summarymapsjournal.com/",
            "publisher": { "@type": "Organization", "name": "SummaryMaps" },
          },
        ]}
      />
      <main className="relative z-10 overflow-x-clip">
        <Hero />
        <HowItWorks />
        <ArticlesGrid />
        <About />
        <Newsletter />
      </main>
    </div>
  );
}
